import {
  ChangePasswordApiRequest,
  ForgotPasswordRequest,
  LoginApiRequest,
  RequestLibertaOTP,
} from '@/data/payload/api/AuthRequest'
import {
  ChangePasswordRequestInterface,
  ExchangeOTPRequestInterface,
  ForgotPasswordRequestInterface,
  ForgotPasswordVerifyRequestInterface,
  LoginRequestInterface,
  ProfileMfaOtpRequestInterface,
  ResetPasswordRequestInterface,
} from '@/data/payload/contracts/AuthRequest'
import ApiService from '@/app/infrastructures/services/ApiService'
import { AuthDataMapper } from '@/data/persistences/mappers/AuthDataMapper'
import { AuthRepositoryInterface } from '@/data/persistences/repositories/contracts/AuthRepositoryInterface'
import { Account, OTP, OTP2FA, ProfileMfaOtpResponse, Token, TwoFactorAuthLogin } from '@/domain/entities/Account'
import { Endpoints } from '@/app/infrastructures/misc/Endpoints'
import { Utils } from '../../misc'

export class AuthApiRepository implements AuthRepositoryInterface {
  private service: ApiService
  private mapper: AuthDataMapper

  constructor(service: ApiService, mapper: AuthDataMapper) {
    this.service = service
    this.mapper = mapper
  }

  public async getMe(): Promise<Account> {
    const resp = await this.service.invoke(
      'get',
      Endpoints.Route.CMS_MY_PROFILE
    )
    return this.mapper.convertMyProfileFromApi(resp)
  }

  public async login(payload: LoginRequestInterface): Promise<Token> {
    const resp = await this.service.invoke(
      'post',
      `${Endpoints.Route.CMS_AUTH_V2}/login`,
      undefined,
      payload as LoginApiRequest
    )
    return this.mapper.convertAuthFromApi(resp)
  }

  public async login2fa(payload: LoginApiRequest): Promise<TwoFactorAuthLogin> {
    const resp = await this.service.invoke(
      'post',
      Endpoints.Route.LOGIN_2FA,
      undefined,
      payload as LoginApiRequest
    )

    return this.mapper.convertAuth2FAFromApi(resp);
  }

  public async changePassword(
    payload: ChangePasswordRequestInterface
  ): Promise<boolean> {
    await this.service.invoke(
      'post',
      `${Endpoints.Route.CMS_AUTH}/me/password`,
      undefined,
      payload as ChangePasswordApiRequest
    )
    return true
  }

  public async forgotPassword(
    payload: ForgotPasswordRequest
  ): Promise<boolean> {
    await this.service.invoke(
      'post',
      `${Endpoints.Route.CMS_AUTH}/forgot_password`,
      undefined,
      payload as ForgotPasswordRequestInterface,
      new Map([
        ["X-captcha", payload.token]
      ])
    )
    return true
  }

  public async forgotPasswordVerify(
    payload: ForgotPasswordVerifyRequestInterface
  ): Promise<boolean> {
    await this.service.invoke(
      'post',
      `${Endpoints.Route.CMS_AUTH}/forgot_password/verify`,
      undefined,
      payload as ForgotPasswordVerifyRequestInterface
    )
    return true
  }

  public async resetPassword(
    payload: ResetPasswordRequestInterface
  ): Promise<boolean> {
    await this.service.invoke(
      'post',
      `${Endpoints.Route.CMS_AUTH}/password_reset/liberta`,
      undefined,
      payload as ResetPasswordRequestInterface
    )
    return true
  }

  public async requestLibertaOTP(payload: RequestLibertaOTP): Promise<OTP> {
    const resp = await this.service.invoke(
      "post",
      Endpoints.Route.REQUEST_LIBERTA_OTP,
      undefined,
      payload,
      new Map([
        ["X-Captcha", payload.token]
      ])
    )

    return this.mapper.convertRequestLibertaOTPFromApi(resp);
  }

  public async exchangeOTP(payload: ExchangeOTPRequestInterface): Promise<string> {
    const resp = await this.service.invoke(
      "post",
      Endpoints.Route.EXCHANGE_OTP,
      undefined,
      payload
    )

    return resp.data.token;
  }

  public async request2FAOTP(payload: RequestLibertaOTP): Promise<OTP2FA> {
    const resp = await this.service.invoke(
      'post',
      Endpoints.Route.REQUEST_OTP_2FA,
      undefined,
      payload,
      undefined,
      true
    )

    return this.mapper.convertRequestOTP2FAFromApi(resp);
  }

  public async verify2FAOTP(payload: ExchangeOTPRequestInterface): Promise<Token> {
    const resp = await this.service.invoke(
      'post',
      Endpoints.Route.VERIFY_OTP_2FA,
      undefined,
      payload,
      undefined,
      true
    )
    return this.mapper.convertAuthFromApi(resp)
  }

  public async requestProfileMfaOtp(payload: ProfileMfaOtpRequestInterface): Promise<ProfileMfaOtpResponse> {
    const resp = await this.service.invoke(
      "post",
      Endpoints.Route.ACCOUNT_2FA_OTP_REQUEST,
      undefined,
      payload
    )

    return this.mapper.convertProfileMfaOtpFromApi(resp)
  }

  public async verifyProfileMfaOtp(payload: ExchangeOTPRequestInterface): Promise<boolean> {
    const resp = await this.service.invoke(
      "post",
      Endpoints.Route.ACCOUNT_2FA_OTP_VERIFY,
      undefined,
      payload
    )

    return this.mapper.convertProfileMfaVerifyFromApi(resp)
  }
}

